import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, Upload as UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function MyChannel() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Upload form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    visibility: 'public',
    price: ''
  });
  const [file, setFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [chRes, vidRes] = await Promise.all([
          axios.get('/api/channels/me').catch((e) => e.response),
          axios.get('/api/channels/me/videos')
        ]);
        if (chRes?.status === 200 && chRes.data?.channel) {
          setChannel(chRes.data.channel);
        }
        if (vidRes?.status === 200) {
          setVideos(vidRes.data.videos || []);
        }
      } catch (e) {
        setError('Failed to load channel data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDeleteChannel = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your channel and all its videos? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setError('');
      await axios.delete('/api/channels/me');
      setChannel(null);
      setVideos([]);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete channel');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    const confirmed = window.confirm('Are you sure you want to delete this video? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await axios.delete(`/api/videos/${videoId}`);
      // Refresh list after delete
      const vidRes = await axios.get('/api/channels/me/videos');
      setVideos(vidRes.data.videos || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete video');
    }
  };

  const handleThumbnailChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Thumbnail must be an image file');
        return;
      }
      setThumbnailFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setUploadSuccess(false);

    if (!channel) {
      setError('Please create your channel first');
      return;
    }
    if (!file) {
      setError('Please select a video file');
      return;
    }
    if (!thumbnailFile) {
      setError('Please select a thumbnail image');
      return;
    }
    if (!formData.title.trim()) {
      setError('Please enter a video title');
      return;
    }

    if (formData.visibility === 'private' && !formData.price.trim()) {
      setError('Please set a price for private videos');
      return;
    }

    const uploadData = new FormData();
    try {
      setUploading(true);
      // 1) Get a presigned upload URL for the video
      const presignRes = await axios.post('/api/videos/upload-url', {
        fileName: file.name,
        contentType: file.type || 'video/mp4',
      });

      const { uploadUrl, key: videoKey } = presignRes.data;

      // 2) Upload the video file directly to R2
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Video upload to storage failed');
      }

      // 3) Upload thumbnail + metadata to backend, referencing the videoKey
      uploadData.append('thumbnail', thumbnailFile);
      uploadData.append('title', formData.title);
      uploadData.append('description', formData.description);
      uploadData.append('visibility', formData.visibility);
      uploadData.append('videoKey', videoKey);
      if (formData.visibility === 'private' && formData.price) {
        uploadData.append('price', formData.price);
      }

      await axios.post('/api/videos/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadSuccess(true);
      // Refresh list
      const vidRes = await axios.get('/api/channels/me/videos');
      setVideos(vidRes.data.videos || []);
      // Reset form
      setFormData({ title: '', description: '', visibility: 'public', price: '' });
      setFile(null);
      setThumbnailFile(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload video');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadSuccess(false), 2000);
    }
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!channel) {
    return (
      <div style={{ padding: 24 }}>
        <h2>You dont have a channel yet</h2>
        <p>Create your channel to start uploading videos.</p>
        <button className="btn btn-primary" onClick={() => navigate('/create-channel')}>Create Channel</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 8 }}>{channel.name}</h2>
      <p style={{ marginTop: 0, color: '#666' }}>{channel.description || 'No description yet'}</p>

      <div style={{ margin: '12px 0 24px', display: 'flex', gap: 12 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/create-channel')}
        >
          Edit Channel
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ backgroundColor: '#b91c1c', borderColor: '#b91c1c' }}
          onClick={handleDeleteChannel}
        >
          Delete Channel
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <h3 style={{ marginBottom: 12 }}>Upload new video</h3>
          <form onSubmit={handleUpload} className="upload-form">
            <div className="form-group">
              <label htmlFor="video-file" className="file-upload-label">
                <div className="file-upload-area">
                  {file ? (
                    <>
                      <CheckCircle size={48} color="#00ff00" />
                      <p className="file-name">{file.name}</p>
                      <p className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </>
                  ) : (
                    <>
                      <UploadIcon size={48} />
                      <p>Click to select video file</p>
                      <p className="file-hint">MP4, AVI, MOV, MKV, WEBM (Max 500MB)</p>
                    </>
                  )}
                </div>
                <input type="file" id="video-file" accept="video/*" onChange={handleFileChange} className="file-input" />
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input type="text" id="title" name="title" className="input" value={formData.title} onChange={handleInputChange} placeholder="Enter video title" required />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea id="description" name="description" className="input textarea" value={formData.description} onChange={handleInputChange} placeholder="Tell viewers about your video" rows="5" />
            </div>

            <div className="form-group">
              <label htmlFor="visibility">Visibility</label>
              <select id="visibility" name="visibility" className="input" value={formData.visibility} onChange={handleInputChange}>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            {formData.visibility === 'private' && (
              <div className="form-group">
                <label htmlFor="price">Price</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  className="input"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="Set a price for this private video"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="thumbnail-file">Thumbnail image *</label>
              <input
                type="file"
                id="thumbnail-file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {uploadSuccess && <div style={{ color: '#0a7f33' }}>Uploaded!</div>}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={uploading || !file}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </form>
        </div>

        <div>
          <h3 style={{ marginBottom: 12 }}>Your videos</h3>
          {videos.length === 0 ? (
            <div>No videos yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {videos.map((v) => (
                <div key={v._id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{v.title}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>Visibility: {v.visibility}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleDeleteVideo(v._id)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyChannel;
